/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
type WheelDetail = {
  x: number;
  y: number;
  deltaX: number;
  deltaY: number;
  ctrlKey: boolean;
};

type UseWheelOptions = {
  onWheel: (detail: WheelDetail) => void;
  preventDefault?: boolean;
  isDisabled?: boolean;
};

const PX_FACTOR_PER_LINE = 15; // Value used in Toolkit
const PX_FACTOR_PER_PAGE = 40; // Value copied from datagrid; this is not handled in Toolkit

const getNormalizedDelta = (e: WheelEvent) => {
  const { deltaX, deltaY, deltaMode } = e;

  switch (deltaMode) {
    case e.DOM_DELTA_PIXEL:
      return { deltaX: -deltaX, deltaY: -deltaY };
    case e.DOM_DELTA_LINE:
      return { deltaX: -deltaX * PX_FACTOR_PER_LINE, deltaY: -deltaY * PX_FACTOR_PER_LINE };
    case e.DOM_DELTA_PAGE:
      return { deltaX: -deltaX * PX_FACTOR_PER_PAGE, deltaY: -deltaY * PX_FACTOR_PER_PAGE };
    default:
      return { deltaX: -deltaX, deltaY: -deltaY };
  }
};

/**
 * Hook that enables and handles wheel gestures.
 */
const useWheel = ({
  onWheel: onWheelCallback,
  preventDefault = true,
  isDisabled = false
}: UseWheelOptions) => {
  const onWheel = (e: WheelEvent) => {
    const { deltaX, deltaY } = getNormalizedDelta(e);
    if (preventDefault) e.preventDefault();
    onWheelCallback({
      x: e.offsetX,
      y: e.offsetY,
      deltaX,
      deltaY,
      ctrlKey: e.ctrlKey || e.metaKey
    });
  };
  return isDisabled ? {} : { onWheel };
};

export { useWheel, PX_FACTOR_PER_LINE };
